/**
 * Click tracking counts — data layer (feature #167).
 *
 * Thin typed wrappers over `apiClient` for the click-counts surface at
 * /api/v1/click-counts. No React / TanStack imports in this module so it can
 * be unit-tested independently of the page.
 *
 * Endpoints:
 *   POST   /click-counts                 Ingest a single click
 *   GET    /click-counts/summary         Aggregate counts (unique/gross/invalid)
 *   GET    /click-counts/breakdown       Grouped by offer + affiliate
 *   GET    /click-counts/:id             Fetch a single row (debug)
 *
 * `unique`  — distinct click_ids observed within the filter window.
 * `gross`   — raw count of every ingestion attempt (= unique + invalid).
 * `invalid` — rejections (bot | dedupe | malformed | other).
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export const INVALID_REASONS = [
	"bot",
	"dedupe",
	"malformed",
	"other",
] as const;
export type InvalidReason = (typeof INVALID_REASONS)[number];

export type Classification = "unique" | "invalid";

export interface InvalidByReason {
	bot: number;
	dedupe: number;
	malformed: number;
	other: number;
}

export interface SummaryResponse {
	from: number | null;
	to: number | null;
	offer_id: string | null;
	affiliate_id: string | null;
	unique: number;
	gross: number;
	invalid: number;
	invalid_by_reason: InvalidByReason;
}

export interface BreakdownBucket {
	unique: number;
	gross: number;
	invalid: number;
}

export interface OfferBucket extends BreakdownBucket {
	offer_id: string;
}

export interface AffiliateBucket extends BreakdownBucket {
	affiliate_id: string;
}

export interface BreakdownResponse {
	from: number | null;
	to: number | null;
	by_offer: OfferBucket[];
	by_affiliate: AffiliateBucket[];
}

export interface IngestInput {
	clickId: string;
	offerId?: string;
	affiliateId?: string;
	bot?: boolean;
	malformed?: boolean;
	occurredAt?: number;
}

export interface IngestResult {
	id: string;
	click_id: string;
	classification: Classification;
	reason: string;
}

export interface SummaryFilters {
	from?: number;
	to?: number;
	offerId?: string;
	affiliateId?: string;
}

// ---------------------------------------------------------------------------
// Pure form / validation helpers — exported for tests so the page test can
// assert behaviour without mounting DOM.
// ---------------------------------------------------------------------------

export interface IngestFormInput {
	clickId?: unknown;
	offerId?: unknown;
	affiliateId?: unknown;
	bot?: unknown;
	malformed?: unknown;
	occurredAt?: unknown;
}

export interface IngestParseOk {
	success: true;
	data: IngestInput;
}
export interface IngestParseErr {
	success: false;
	errors: Record<string, string>;
}
export type IngestParseResult = IngestParseOk | IngestParseErr;

const CLICK_ID_MAX = 200;
const SCOPE_ID_MAX = 120;

export const ingestFormSchema = {
	safeParse(input: IngestFormInput): IngestParseResult {
		const errors: Record<string, string> = {};

		const clickIdRaw =
			typeof input.clickId === "string" ? input.clickId.trim() : "";
		if (!clickIdRaw) {
			errors.clickId = "Click ID is required.";
		} else if (clickIdRaw.length > CLICK_ID_MAX) {
			errors.clickId = `Click ID must be ${CLICK_ID_MAX} characters or fewer.`;
		}

		const offerRaw =
			typeof input.offerId === "string" ? input.offerId.trim() : "";
		if (offerRaw.length > SCOPE_ID_MAX) {
			errors.offerId = `Offer ID must be ${SCOPE_ID_MAX} characters or fewer.`;
		}

		const affiliateRaw =
			typeof input.affiliateId === "string" ? input.affiliateId.trim() : "";
		if (affiliateRaw.length > SCOPE_ID_MAX) {
			errors.affiliateId = `Affiliate ID must be ${SCOPE_ID_MAX} characters or fewer.`;
		}

		let occurredAt: number | undefined;
		if (
			input.occurredAt !== undefined &&
			input.occurredAt !== null &&
			input.occurredAt !== ""
		) {
			const n =
				typeof input.occurredAt === "number"
					? input.occurredAt
					: Number(input.occurredAt);
			if (
				!Number.isFinite(n) ||
				n < 0 ||
				n > 9_999_999_999 ||
				Math.trunc(n) !== n
			) {
				errors.occurredAt =
					"Occurred at must be an integer UNIX timestamp (seconds).";
			} else {
				occurredAt = n;
			}
		}

		if (Object.keys(errors).length > 0) {
			return { success: false, errors };
		}

		return {
			success: true,
			data: {
				clickId: clickIdRaw,
				offerId: offerRaw || undefined,
				affiliateId: affiliateRaw || undefined,
				bot: input.bot === true,
				malformed: input.malformed === true,
				occurredAt,
			},
		};
	},
};

// ---------------------------------------------------------------------------
// Pure presentation helpers
// ---------------------------------------------------------------------------

/**
 * Compute the "invalid rate" — invalid / gross, rounded to 1 dp. Returns 0
 * when gross is 0 to avoid NaN in the UI.
 */
export function invalidRate(gross: number, invalid: number): number {
	if (!Number.isFinite(gross) || gross <= 0) return 0;
	const pct = (invalid / gross) * 100;
	return Math.round(pct * 10) / 10;
}

/**
 * Compute the "unique rate" — unique / gross, rounded to 1 dp.
 */
export function uniqueRate(gross: number, unique: number): number {
	if (!Number.isFinite(gross) || gross <= 0) return 0;
	const pct = (unique / gross) * 100;
	return Math.round(pct * 10) / 10;
}

/**
 * Format an integer with thousands separators. Pure, locale-stable (en-US).
 */
export function formatCount(n: number): string {
	if (!Number.isFinite(n)) return "0";
	return new Intl.NumberFormat("en-US").format(Math.trunc(n));
}

/**
 * Convert a UNIX-seconds timestamp to an ISO-ish short label. Empty string
 * if the input is null / zero.
 */
export function formatUnixSecShort(ts: number | null | undefined): string {
	if (ts === null || ts === undefined || ts === 0) return "";
	const d = new Date(ts * 1000);
	if (Number.isNaN(d.getTime())) return "";
	const iso = d.toISOString();
	return `${iso.slice(0, 10)} ${iso.slice(11, 16)}`;
}

/**
 * Build a stable label for a scope key ("" → "unscoped").
 */
export function scopeLabel(raw: string): string {
	if (!raw) return "unscoped";
	return raw;
}

// ---------------------------------------------------------------------------
// HTTP helpers
// ---------------------------------------------------------------------------

export async function ingestClick(input: IngestInput): Promise<IngestResult> {
	const body: Record<string, unknown> = { click_id: input.clickId };
	if (input.offerId !== undefined) body.offer_id = input.offerId;
	if (input.affiliateId !== undefined) body.affiliate_id = input.affiliateId;
	if (input.bot !== undefined) body.bot = input.bot;
	if (input.malformed !== undefined) body.malformed = input.malformed;
	if (input.occurredAt !== undefined) body.occurred_at = input.occurredAt;
	return apiClient.post<IngestResult>("/click-counts", body);
}

export async function fetchClickSummary(
	filters: SummaryFilters = {},
): Promise<SummaryResponse | null> {
	const query: Record<string, string> = {};
	if (filters.from !== undefined) query.from = String(filters.from);
	if (filters.to !== undefined) query.to = String(filters.to);
	if (filters.offerId !== undefined && filters.offerId !== "")
		query.offer_id = filters.offerId;
	if (filters.affiliateId !== undefined && filters.affiliateId !== "")
		query.affiliate_id = filters.affiliateId;
	try {
		return await apiClient.get<SummaryResponse>(
			"/click-counts/summary",
			Object.keys(query).length > 0 ? { query } : undefined,
		);
	} catch (err) {
		if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
			return null;
		}
		throw err;
	}
}

export async function fetchClickBreakdown(
	filters: Pick<SummaryFilters, "from" | "to"> = {},
): Promise<BreakdownResponse | null> {
	const query: Record<string, string> = {};
	if (filters.from !== undefined) query.from = String(filters.from);
	if (filters.to !== undefined) query.to = String(filters.to);
	try {
		return await apiClient.get<BreakdownResponse>(
			"/click-counts/breakdown",
			Object.keys(query).length > 0 ? { query } : undefined,
		);
	} catch (err) {
		if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
			return null;
		}
		throw err;
	}
}

// ---------------------------------------------------------------------------
// Paths (stable strings the page uses for doc hints + deep links).
// ---------------------------------------------------------------------------

export const clickCountsPaths = {
	base: "/click-counts",
	summary: "/click-counts/summary",
	breakdown: "/click-counts/breakdown",
} as const;
